'use client';

import { useState, useEffect } from 'react';
import axios from 'axios';
import Link from 'next/link';
import { NewsItem } from '@/types/news';
import { EventItem } from '@/types/event';
import { ApiResponse } from '@/types/api';
import { useTranslation } from '@/context/LanguageContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { faCalendarTimes, faClock, faNewspaper } from '@fortawesome/free-regular-svg-icons';

interface Announcement {
  id: string;
  slug: string;
  type: string;
  typeColor: string;
  title: string;
  date: string;
}

interface AgendaEvent {
  id: string;
  slug: string;
  title: string;
  date: string;
  month: string;
  location: string;
  color: string;
  is_online: boolean;
}

import { useUnit } from '@/context/UnitContext';

// ...

export default function AgendaSection() {
  const { t, language } = useTranslation();
  const { unitId } = useUnit();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [agendaEvents, setAgendaEvents] = useState<AgendaEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(true);

  useEffect(() => {
    if (!unitId) {
      setLoading(false);
      return;
    }

    const fetchAnnouncements = async () => {
      try {
        const response = await axios.post<ApiResponse<NewsItem[]>>('/api/doServices', {
          method: 'GET',
          endpoint: `web/news?limit=3&page=1&type=2&unit_id=${unitId}&order=PUBLISHED_DATE DESC`
        });

        const apiData = response.data;

        if (apiData && apiData.data) {
          const mapped: Announcement[] = apiData.data.map((item: NewsItem) => ({
            id: item.id.toString(),
            slug: item.slug,
            type: t('announcement.label'),
            typeColor: 'bg-unmulYellow text-unmulDarkGreen',
            title: language === 'id' ? (item.title_idn || item.title_en) : (item.title_en || item.title_idn) || '',
            date: new Date(item.published_date).toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })
          }));
          setAnnouncements(mapped);
        }
      } catch (error) {
        console.error('Error fetching announcements:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnnouncements();
  }, [language, t, unitId]);

  useEffect(() => {
    if (!unitId) {
      setEventsLoading(false);
      return;
    }

    const fetchEvents = async () => {
      try {
        const response = await axios.post<ApiResponse<EventItem[]>>('/api/doServices', {
          method: 'GET',
          endpoint: `web/event?limit=3&page=1&unit_id=${unitId}&order=CREATED_AT DESC`
        });

        const apiData = response.data;

        if (apiData && apiData.data) {
          const mapped: AgendaEvent[] = apiData.data.map((item: EventItem) => {
            const startDate = new Date(item.start_time);
            const colors = [
              'bg-unmulGreen text-white',
              'bg-unmulYellow text-unmulDarkGreen',
              'bg-blue-700 text-white',
              'bg-purple-700 text-white',
              'bg-teal-700 text-white'
            ];
            
            return {
              id: item.id.toString(),
              slug: item.slug,
              title: language === 'id' ? (item.title_idn || item.title_en) : (item.title_en || item.title_idn) || '',
              date: startDate.getDate().toString().padStart(2, '0'),
              month: startDate.toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US', { month: 'short', year: 'numeric' }),
              location: item.unit_name || 'Lokasi TBA',
              color: colors[parseInt(item.id.toString()) % colors.length],
              is_online: false
            };
          });
          setAgendaEvents(mapped);
        }
      } catch (error: any) {
        console.error('Error fetching events:', error);
      } finally {
        setEventsLoading(false);
      }
    };

    fetchEvents();
  }, [language, unitId]);

  return (
    <section className="py-20 bg-unmulPale/30">
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-12">
          
          {/* Column 1: Agenda */}
          <div>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl font-serif font-bold text-gray-800 mb-2">
                  {t('agenda.title')} <span className="text-unmulYellow">.</span>
                </h2>
                <p className="text-gray-600">{t('agenda.subtitle')}</p>
              </div>
              <Link href="/event" className="text-unmulGreen font-medium hover:underline flex items-center gap-2">
                {t('agenda.viewAll')}
                <FontAwesomeIcon icon={faArrowRight} />
              </Link>
            </div>

            <div className="space-y-4">
              {eventsLoading ? (
                <div className="text-center py-10 opacity-60">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-unmulGreen mx-auto mb-2"></div>
                  {t('common.loading')}
                </div>
              ) : agendaEvents.length > 0 ? (
                agendaEvents.map((item) => (
                  <Link key={item.id} href={`/event/${item.slug}`} className="block group">
                    <div className="bg-white p-5 rounded-xl shadow-sm hover:shadow-md transition border border-gray-100 flex gap-5 items-center">
                      <div className={`w-20 h-20 rounded-lg flex flex-col items-center justify-center shrink-0 ${item.color}`}>
                        <span className="text-2xl font-bold">{item.date}</span>
                        <span className="text-xs uppercase font-medium">{item.month}</span>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-gray-800 group-hover:text-unmulGreen transition line-clamp-2">
                          {item.title}
                        </h3>
                        <div className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                          <span><FontAwesomeIcon icon={faMapMarkerAlt} className="text-unmulYellow mr-1" /> {item.location}</span>
                          {item.is_online && (
                             <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">Online</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="text-center py-10 text-gray-400 bg-white rounded-xl border border-dashed border-gray-300">
                  <FontAwesomeIcon icon={faCalendarTimes} className="text-3xl mb-2 block" />
                  {t('agenda.noEvents')}
                </div>
              )}
            </div>
          </div>

          {/* Column 2: Pengumuman */}
          <div>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl font-serif font-bold text-gray-800 mb-2">
                  {t('announcement.title')} <span className="text-unmulGreen">.</span>
                </h2>
                <p className="text-gray-600">{t('announcement.subtitle')}</p>
              </div>
              <Link href="/pengumuman" className="text-unmulGreen font-medium hover:underline flex items-center gap-2">
                {t('announcement.viewAll')}
                <FontAwesomeIcon icon={faArrowRight} />
              </Link>
            </div>

            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-10 opacity-60">
                   <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-unmulGreen mx-auto mb-2"></div>
                   {t('common.loading')}
                </div>
              ) : announcements.length > 0 ? (
                announcements.map((item) => (
                  <Link key={item.id} href={`/pengumuman/${item.slug}`} className="block group">
                    <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition border border-gray-100 relative overflow-hidden">
                      <div className="absolute top-0 left-0 w-1 h-full bg-unmulYellow"></div>
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <span className={`inline-block px-2 py-1 rounded text-xs font-bold mb-2 ${item.typeColor}`}>
                            {item.type}
                          </span>
                          <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-unmulGreen transition line-clamp-2">
                            {item.title}
                          </h3>
                          <p className="text-sm text-gray-500">
                            <FontAwesomeIcon icon={faClock} className="mr-1" /> {item.date}
                          </p>
                        </div>
                        <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-unmulGreen group-hover:text-white transition">
                          <FontAwesomeIcon icon={faArrowRight} className="text-sm" />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="text-center py-10 text-gray-400 bg-white rounded-xl border border-dashed border-gray-300">
                  <FontAwesomeIcon icon={faNewspaper} className="text-3xl mb-2 block" />
                  {t('announcement.noAnnouncements')}
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
